import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Rating,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  Collapse,
  Grid,
  LinearProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ContentCopy,
  Check,
  ExpandMore,
  ExpandLess,
  CloudUpload,
  MusicNote,
  Lyrics as LyricsIcon,
  Style,
  CheckCircle,
  Search,
  Clear,
  LibraryMusic,
  AutoAwesome,
  Assessment,
  Description,
  LinkOff,
} from '@mui/icons-material';
import {
  useAIQueue,
  useCreateAIQueueItem,
  useUpdateAIQueueItem,
  useDeleteAIQueueItem,
  useLinkAIContent,
  useUnlinkAIContent,
} from '../../hooks/useAIGeneration';
import { useContentUpload } from '../../hooks/useContent';
import type { AIGenerationQueueItem, AIGenerationStatus, UploadProgress } from '../../api/types';
import { useOrgFilter } from '../../contexts/OrganizationContext';
import { ReferencesSection } from './ReferencesSection';
import { GeneratorSection } from './GeneratorSection';
import { TemplatesSection } from './TemplatesSection';
import { CoverageSection } from './CoverageSection';

const SONGFORGE_ACCENT_COLOR = '#8b5cf6';

const STATUS_COLORS: Record<AIGenerationStatus, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning'> = {
  pending: 'default',
  prompt_ready: 'primary',
  generated: 'warning',
  uploaded: 'success',
  failed: 'error',
};

const STATUS_LABELS: Record<AIGenerationStatus, string> = {
  pending: 'Pending',
  prompt_ready: 'Prompt Ready',
  generated: 'Generated',
  uploaded: 'Uploaded',
  failed: 'Failed',
};

interface QueueFilters {
  search: string;
  genre: string;
  mood: string;
  status: AIGenerationStatus | '';
  energyRange: [number, number];
  bpmMin: string;
  bpmMax: string;
}

const defaultFilters: QueueFilters = {
  search: '',
  genre: '',
  mood: '',
  status: '',
  energyRange: [1, 10],
  bpmMin: '',
  bpmMax: '',
};

interface QueueCardProps {
  item: AIGenerationQueueItem;
  onEdit: (item: AIGenerationQueueItem) => void;
  onDelete: (id: string) => void;
  onCopy: (text: string, type: string) => void;
  onStatusChange: (id: string, status: AIGenerationStatus) => void;
  onUpload: (item: AIGenerationQueueItem) => void;
  onUnlink: (item: AIGenerationQueueItem) => void;
  isUploading: boolean;
  uploadProgress: number;
}

const QueueCard: React.FC<QueueCardProps> = ({
  item,
  onEdit,
  onDelete,
  onCopy,
  onStatusChange,
  onUpload,
  onUnlink,
  isUploading,
  uploadProgress,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (item.status === 'generated' || item.status === 'prompt_ready') {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (item.status === 'generated' || item.status === 'prompt_ready') {
      onUpload(item);
    }
  };

  const canUpload = item.status === 'generated' || item.status === 'prompt_ready';

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: isDragOver ? `2px dashed ${SONGFORGE_ACCENT_COLOR}` : '1px solid rgba(255,255,255,0.1)',
        bgcolor: isDragOver ? 'rgba(139, 92, 246, 0.1)' : 'background.paper',
        transition: 'all 0.2s ease',
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Header with Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Chip
            label={STATUS_LABELS[item.status]}
            color={STATUS_COLORS[item.status]}
            size="small"
          />
          <Tooltip title="Copy Styles">
            <IconButton size="small" onClick={() => onCopy(item.style_prompt || '', 'Styles')}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Styles Section - with scrollbar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Style fontSize="small" color="primary" />
            <Typography variant="subtitle2" color="primary">
              Styles
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: 'rgba(139, 92, 246, 0.08)',
              p: 1.5,
              borderRadius: 1,
              maxHeight: 120,
              overflow: 'auto',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {item.style_prompt || 'No styles defined'}
            </Typography>
          </Box>
        </Box>

        {/* Expandable Lyrics Section */}
        {item.lyrics && (
          <Box>
            <Button
              size="small"
              startIcon={<LyricsIcon />}
              endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setExpanded(!expanded)}
              sx={{ mb: 1 }}
            >
              {expanded ? 'Hide Lyrics' : 'Show Lyrics'}
            </Button>
            <Collapse in={expanded}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.03)',
                  p: 1.5,
                  borderRadius: 1,
                  maxHeight: 200,
                  overflow: 'auto',
                  position: 'relative',
                }}
              >
                <Tooltip title="Copy Lyrics">
                  <IconButton
                    size="small"
                    onClick={() => onCopy(item.lyrics!, 'Lyrics')}
                    sx={{ position: 'absolute', top: 4, right: 4 }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}
                >
                  {item.lyrics}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Uploading... {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 0.5 }} />
          </Box>
        )}

        {/* Drag Drop Zone for generated items */}
        {canUpload && !isUploading && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: '2px dashed rgba(139, 92, 246, 0.3)',
              borderRadius: 2,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: SONGFORGE_ACCENT_COLOR,
                bgcolor: 'rgba(139, 92, 246, 0.05)',
              },
            }}
            onClick={() => onUpload(item)}
          >
            <CloudUpload sx={{ fontSize: 32, color: 'text.secondary', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              Drop audio file here or click to upload
            </Typography>
          </Box>
        )}

        {/* Uploaded indicator with unlink option */}
        {item.status === 'uploaded' && item.generated_content_id && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
              <CheckCircle fontSize="small" />
              <Typography variant="body2">Content linked</Typography>
            </Box>
            <Tooltip title="Unlink audio">
              <IconButton
                size="small"
                color="warning"
                onClick={() => onUnlink(item)}
              >
                <LinkOff fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Rating */}
        {item.quality_rating && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">Rating:</Typography>
            <Rating value={item.quality_rating} readOnly size="small" />
          </Box>
        )}
      </CardContent>

      <Divider />

      <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
        <Box>
          {item.status === 'prompt_ready' && (
            <Tooltip title="Mark as Generated">
              <IconButton
                size="small"
                color="warning"
                onClick={() => onStatusChange(item.id, 'generated')}
              >
                <Check />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(item)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDelete(item.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
};

// Extract unique values from queue items for filter dropdowns
function extractFilterOptions(items: AIGenerationQueueItem[] | undefined) {
  const genres = new Set<string>();
  const moods = new Set<string>();

  items?.forEach((item) => {
    if (item.target_genre) genres.add(item.target_genre);
    if (item.target_mood) moods.add(item.target_mood);
  });

  return {
    genres: Array.from(genres).sort(),
    moods: Array.from(moods).sort(),
  };
}

// Extract BPM from style_tags (e.g., "120bpm" or "120 bpm")
function extractBpmFromTags(tags: string | undefined): number | null {
  if (!tags) return null;
  const match = tags.match(/(\d+)\s*bpm/i);
  return match ? parseInt(match[1], 10) : null;
}

// Filter queue items based on filters
function filterQueueItems(items: AIGenerationQueueItem[] | undefined, filters: QueueFilters): AIGenerationQueueItem[] {
  if (!items) return [];

  return items.filter((item) => {
    // Text search (theme, lyrics, style_prompt)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesTheme = item.theme?.toLowerCase().includes(searchLower);
      const matchesLyrics = item.lyrics?.toLowerCase().includes(searchLower);
      const matchesPrompt = item.style_prompt?.toLowerCase().includes(searchLower);
      if (!matchesTheme && !matchesLyrics && !matchesPrompt) return false;
    }

    // Genre filter
    if (filters.genre && item.target_genre !== filters.genre) return false;

    // Mood filter
    if (filters.mood && item.target_mood !== filters.mood) return false;

    // Status filter (handled by API, but also filter client-side for immediate feedback)
    // Auto-hide 'uploaded' items unless explicitly filtered for them
    if (filters.status) {
      if (item.status !== filters.status) return false;
    } else {
      // No status filter set - hide uploaded items (they belong in Content Library now)
      if (item.status === 'uploaded') return false;
    }

    // Energy range filter
    if (item.target_energy) {
      if (item.target_energy < filters.energyRange[0] || item.target_energy > filters.energyRange[1]) {
        return false;
      }
    }

    // BPM range filter
    const itemBpm = extractBpmFromTags(item.style_tags);
    if (itemBpm !== null) {
      if (filters.bpmMin && itemBpm < parseInt(filters.bpmMin, 10)) return false;
      if (filters.bpmMax && itemBpm > parseInt(filters.bpmMax, 10)) return false;
    }

    return true;
  });
}

const SongForge: React.FC = () => {
  // Filter state
  const [filters, setFilters] = useState<QueueFilters>(defaultFilters);
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Accordion state
  const [expandedSection, setExpandedSection] = useState<string | false>(false);

  // Queue dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AIGenerationQueueItem | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [uploadingItemId, setUploadingItemId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [pendingUploadItem, setPendingUploadItem] = useState<AIGenerationQueueItem | null>(null);

  const orgFilter = useOrgFilter();

  // Fetch queue with status filter (other filters are client-side for now)
  const { data: queueItems, isLoading, error } = useAIQueue({
    organization_id: orgFilter,
    status: filters.status || undefined,
  });

  // References query removed - simplified form no longer uses reference song dropdown

  const createMutation = useCreateAIQueueItem();
  const updateMutation = useUpdateAIQueueItem();
  const deleteMutation = useDeleteAIQueueItem();
  const uploadMutation = useContentUpload();
  const linkMutation = useLinkAIContent();
  const unlinkMutation = useUnlinkAIContent();

  // Filter options extracted from queue items
  const filterOptions = useMemo(() => extractFilterOptions(queueItems), [queueItems]);

  // Filtered items (client-side filtering for search, genre, mood, energy, bpm)
  const filteredItems = useMemo(() => filterQueueItems(queueItems, filters), [queueItems, filters]);

  const [formData, setFormData] = useState({
    reference_song_id: '',
    theme: '',
    target_genre: '',
    target_mood: '',
    target_energy: 5,
    style_prompt: '',
    style_tags: '',
    lyrics: '',
    notes: '',
  });

  // Handle search input with debounce
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: value }));
    }, 300);
    setSearchDebounceTimer(timer);
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
    setSearchInput('');
  };

  const hasActiveFilters = filters.search || filters.genre || filters.mood || filters.status ||
    filters.energyRange[0] !== 1 || filters.energyRange[1] !== 10 ||
    filters.bpmMin || filters.bpmMax;

  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const handleOpenCreate = () => {
    setFormData({
      reference_song_id: '',
      theme: '',
      target_genre: '',
      target_mood: '',
      target_energy: 5,
      style_prompt: '',
      style_tags: '',
      lyrics: '',
      notes: '',
    });
    setEditingItem(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (item: AIGenerationQueueItem) => {
    setFormData({
      reference_song_id: item.reference_song_id || '',
      theme: item.theme || '',
      target_genre: item.target_genre || '',
      target_mood: item.target_mood || '',
      target_energy: item.target_energy || 5,
      style_prompt: item.style_prompt || '',
      style_tags: item.style_tags || '',
      lyrics: item.lyrics || '',
      notes: item.notes || '',
    });
    setEditingItem(item);
    setCreateDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        organization_id: orgFilter || '',
        reference_song_id: formData.reference_song_id || undefined,
      };

      if (editingItem) {
        await updateMutation.mutateAsync({ id: editingItem.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      setCreateDialogOpen(false);
    } catch (err) {
      console.error('Failed to save queue item:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this queue item?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setSnackbarMessage(`${type} copied to clipboard`);
    setSnackbarOpen(true);
  };

  const handleUpdateStatus = async (id: string, status: AIGenerationStatus) => {
    await updateMutation.mutateAsync({ id, data: { status } });
  };

  const handleUploadClick = (item: AIGenerationQueueItem) => {
    setPendingUploadItem(item);
    fileInputRef.current?.click();
  };

  const handleUnlink = async (item: AIGenerationQueueItem) => {
    if (!item.generated_content_id) return;

    if (window.confirm('Are you sure you want to remove this audio? This will delete the audio file from the Content Library so you can re-upload the correct track.')) {
      try {
        await unlinkMutation.mutateAsync({
          queueItemId: item.id,
          contentId: item.generated_content_id,
        });
        setSnackbarMessage('Audio removed successfully');
        setSnackbarOpen(true);
      } catch (err) {
        console.error('Unlink failed:', err);
        setSnackbarMessage('Failed to remove audio');
        setSnackbarOpen(true);
      }
    }
  };

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pendingUploadItem) return;

    // Reset input
    e.target.value = '';

    setUploadingItemId(pendingUploadItem.id);
    setUploadProgress(0);

    try {
      // Upload with is_ai_generated flag
      const result = await uploadMutation.mutateAsync({
        uploadRequest: {
          file,
          title: pendingUploadItem.theme || file.name,
          genre: pendingUploadItem.target_genre,
          is_ai_generated: true,
        },
        onProgress: (progress: UploadProgress) => {
          setUploadProgress(progress.percentage);
        },
      });

      // Link the uploaded content to the queue item
      if (result.content_id) {
        await linkMutation.mutateAsync({
          queueItemId: pendingUploadItem.id,
          contentId: result.content_id,
        });
        setSnackbarMessage('Audio uploaded and linked successfully!');
      } else {
        setSnackbarMessage('Audio uploaded successfully!');
      }
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Upload failed:', err);
      setSnackbarMessage('Upload failed. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setUploadingItemId(null);
      setUploadProgress(0);
      setPendingUploadItem(null);
    }
  }, [pendingUploadItem, uploadMutation, linkMutation]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load queue: {(error as Error)?.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ p: 3, pb: 0 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: SONGFORGE_ACCENT_COLOR, mb: 1 }}>
          SongForge
        </Typography>
        <Typography variant="body1" color="text.secondary">
          AI music generation workflow - manage your queue, references, and templates
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {/* Search and Filter Bar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          {/* Search Row */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search themes, lyrics, prompts..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => handleSearchChange('')}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Filter Row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Genre</InputLabel>
              <Select
                value={filters.genre}
                label="Genre"
                onChange={(e) => setFilters({ ...filters, genre: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions.genres.map((genre) => (
                  <MenuItem key={genre} value={genre}>{genre}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Mood</InputLabel>
              <Select
                value={filters.mood}
                label="Mood"
                onChange={(e) => setFilters({ ...filters, mood: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions.moods.map((mood) => (
                  <MenuItem key={mood} value={mood}>{mood}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value as AIGenerationStatus | '' })}
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ minWidth: 200, px: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Energy: {filters.energyRange[0]} - {filters.energyRange[1]}
              </Typography>
              <Slider
                value={filters.energyRange}
                onChange={(_, value) => setFilters({ ...filters, energyRange: value as [number, number] })}
                min={1}
                max={10}
                size="small"
                valueLabelDisplay="auto"
                sx={{ color: SONGFORGE_ACCENT_COLOR }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">BPM:</Typography>
              <TextField
                size="small"
                placeholder="Min"
                value={filters.bpmMin}
                onChange={(e) => setFilters({ ...filters, bpmMin: e.target.value })}
                sx={{ width: 70 }}
                type="number"
              />
              <Typography variant="caption" color="text.secondary">-</Typography>
              <TextField
                size="small"
                placeholder="Max"
                value={filters.bpmMax}
                onChange={(e) => setFilters({ ...filters, bpmMax: e.target.value })}
                sx={{ width: 70 }}
                type="number"
              />
            </Box>

            {hasActiveFilters && (
              <Button
                size="small"
                startIcon={<Clear />}
                onClick={handleResetFilters}
              >
                Reset All
              </Button>
            )}
          </Box>
        </Paper>

        {/* Actions Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
            {hasActiveFilters && queueItems && ` (filtered from ${queueItems.length})`}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{ bgcolor: SONGFORGE_ACCENT_COLOR }}
          >
            Add to Queue
          </Button>
        </Box>

        {/* Queue Cards Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {filteredItems.map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item.id}>
              <QueueCard
                item={item}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
                onCopy={handleCopy}
                onStatusChange={handleUpdateStatus}
                onUpload={handleUploadClick}
                onUnlink={handleUnlink}
                isUploading={uploadingItemId === item.id}
                uploadProgress={uploadProgress}
              />
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <Paper sx={{ p: 6, textAlign: 'center', mb: 3 }}>
            <MusicNote sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {hasActiveFilters ? 'No matching items' : 'No items in queue'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {hasActiveFilters
                ? 'Try adjusting your filters to see more results.'
                : 'Use the /songforge command in Claude Code to generate songs, or add items manually.'}
            </Typography>
            {hasActiveFilters ? (
              <Button variant="outlined" startIcon={<Clear />} onClick={handleResetFilters}>
                Clear Filters
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleOpenCreate}
                sx={{ bgcolor: SONGFORGE_ACCENT_COLOR }}
              >
                Add to Queue
              </Button>
            )}
          </Paper>
        )}

        {/* Collapsible Sections */}
        <Accordion
          expanded={expandedSection === 'references'}
          onChange={handleAccordionChange('references')}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LibraryMusic sx={{ color: SONGFORGE_ACCENT_COLOR }} />
              <Typography variant="h6">Reference Songs</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <ReferencesSection />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedSection === 'generator'}
          onChange={handleAccordionChange('generator')}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesome sx={{ color: SONGFORGE_ACCENT_COLOR }} />
              <Typography variant="h6">Prompt Generator</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <GeneratorSection />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedSection === 'templates'}
          onChange={handleAccordionChange('templates')}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description sx={{ color: SONGFORGE_ACCENT_COLOR }} />
              <Typography variant="h6">Templates</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <TemplatesSection />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedSection === 'coverage'}
          onChange={handleAccordionChange('coverage')}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment sx={{ color: SONGFORGE_ACCENT_COLOR }} />
              <Typography variant="h6">Coverage & Stats</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <CoverageSection />
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Create/Edit Dialog - Simplified to match Suno.com */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Queue Item' : 'Add to Generation Queue'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Lyrics Section */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LyricsIcon fontSize="small" />
                Lyrics
              </Typography>
              <TextField
                value={formData.lyrics}
                onChange={(e) => setFormData({ ...formData, lyrics: e.target.value })}
                multiline
                rows={10}
                placeholder="Write some lyrics or a prompt — or leave blank for instrumental"
                fullWidth
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                  },
                }}
              />
            </Box>

            {/* Styles Section */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Style fontSize="small" />
                Styles
              </Typography>
              <TextField
                value={formData.style_prompt}
                onChange={(e) => setFormData({ ...formData, style_prompt: e.target.value })}
                multiline
                rows={3}
                placeholder="emotional vocalist, rich melody, arena rock, warm bassline, 120 BPM, major key"
                helperText="Comma-separated style descriptors (genre, mood, instruments, tempo, etc.)"
                fullWidth
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editingItem ? 'Save' : 'Add to Queue'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default SongForge;
